/**
 * AssetCodexPanel — floating panel showing assets owned by the current session
 * participant, with inline sharing controls.
 *
 * Spec: docs/features/session/world-session-panel.md
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { codexService, CodexAsset, CodexSectionResponse } from "../../services/codexService";
import { CampaignAssetService } from "../../services/campaignAssetService";
import { SessionParticipant } from "../../services/sessionService";
import { CampaignAssetDiceTemplate } from "../../types/types";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import CustomAssetReferenceEditor from "../rich-text-editor/CustomAssetReferenceEditor";
import "./SessionScrollbars.css";

// ─── Type badges ──────────────────────────────────────────────────────────────

const TYPE_ICON: Record<string, string> = {
  Character: "🧙",
  Beast: "🐉",
  Quest: "📜",
  NPC: "👤",
  Location: "🏰",
  Item: "💎",
  Lore: "📖",
  Other: "📦",
  World: "🌍",
  GeographicalElement: "⛰️",
  Organization: "🏛️",
  Event: "⚡",
  Encounter: "⚔️",
  Folder: "📁",
};

function typeIcon(t: string) {
  return TYPE_ICON[t] ?? "📦";
}

function errMsg(e: unknown, fallback: string) {
  return e instanceof Error ? e.message : fallback;
}

// ─── Description renderer ─────────────────────────────────────────────────────
// Parses [[asset:ID|Name]] and @[char:ID|Name] markers into styled inline chips.

function renderDescription(text: string): React.ReactNode[] {
  // Matches [[asset:ID|Name]] and @[char:ID|Name] in one pass
  const COMBINED = /\[\[asset:(\d+)\|([^\]]+)\]\]|@\[char:(\d+)\|([^\]]+)\]/g;

  const nodes: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = COMBINED.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    if (match[1] !== undefined) {
      // [[asset:ID|Name]]
      nodes.push(
        <span
          key={`asset-${match[1]}-${match.index}`}
          title={`Asset #${match[1]}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "1px 7px",
            borderRadius: 99,
            background: "rgba(99,102,241,.22)",
            border: "1px solid rgba(165,180,252,.3)",
            color: "#a5b4fc",
            fontSize: 11,
            fontWeight: 600,
            cursor: "default",
            verticalAlign: "middle",
            margin: "0 2px",
          }}
        >
          📦 {match[2]}
        </span>
      );
    } else {
      // @[char:ID|Name]
      nodes.push(
        <span
          key={`char-${match[3]}-${match.index}`}
          title={`Character #${match[3]}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "1px 7px",
            borderRadius: 99,
            background: "rgba(245,158,11,.18)",
            border: "1px solid rgba(245,158,11,.35)",
            color: "#fcd34d",
            fontSize: 11,
            fontWeight: 600,
            cursor: "default",
            verticalAlign: "middle",
            margin: "0 2px",
          }}
        >
          🧙 {match[4]}
        </span>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

// ─── Share Popover ────────────────────────────────────────────────────────────

interface SharePopoverProps {
  participants: SessionParticipant[];
  alreadyShared: number[]; // characterIds already granted
  onShare: (_characterIds: number[]) => Promise<void>;
  onClose: () => void;
  /**
   * When provided the popover is rendered into document.body via a portal and
   * positioned with `position: fixed` relative to these coordinates.
   * This escapes any overflow:hidden ancestor (e.g. the FloatingPanel body).
   */
  anchorRect?: DOMRect;
}

const POPOVER_WIDTH = 230;

const SharePopover: React.FC<SharePopoverProps> = ({
  participants,
  alreadyShared,
  onShare,
  onClose,
  anchorRect,
}) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const targets = participants.filter((p) => p.character_id != null);

  const toggle = (charId: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(charId) ? next.delete(charId) : next.add(charId);
      return next;
    });

  const handleShare = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setError("");
    try {
      await onShare([...selected]);
      onClose();
    } catch (e: unknown) {
      setError(errMsg(e, "Failed to share."));
    } finally {
      setBusy(false);
    }
  };

  // Compute fixed position so it doesn't get clipped by overflow:hidden parents
  const fixedStyle: React.CSSProperties = anchorRect
    ? (() => {
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;
        // prefer below, flip above if not enough room
        const placeAbove = spaceBelow < 200 && spaceAbove > spaceBelow;
        let left = anchorRect.left;
        // keep within viewport
        if (left + POPOVER_WIDTH > window.innerWidth - 8) {
          left = window.innerWidth - POPOVER_WIDTH - 8;
        }
        return {
          position: "fixed" as const,
          top: placeAbove ? undefined : anchorRect.bottom + 4,
          bottom: placeAbove ? window.innerHeight - anchorRect.top + 4 : undefined,
          left,
          zIndex: 9999,
        };
      })()
    : {
        position: "absolute" as const,
        top: "100%",
        left: 0,
        zIndex: 200,
      };

  const popover = (
    <div
      ref={ref}
      style={{
        ...fixedStyle,
        width: POPOVER_WIDTH,
        background: "rgba(10,14,30,0.98)",
        border: "1px solid rgba(165,180,252,.3)",
        borderRadius: 10,
        padding: "10px 12px",
        boxShadow: "0 12px 40px rgba(0,0,0,.75), 0 0 0 1px rgba(99,102,241,.12)",
      }}
    >
      <p
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "rgba(165,180,252,.7)",
          letterSpacing: ".07em",
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        Share with character
      </p>

      {targets.length === 0 && (
        <p style={{ fontSize: 11, color: "rgba(255,255,255,.4)" }}>No other participants.</p>
      )}

      {targets.map((p) => {
        const charId = p.character_id!;
        const alreadyHas = alreadyShared.includes(charId);
        const isSelected = selected.has(charId);
        return (
          <label
            key={charId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 0",
              cursor: alreadyHas ? "default" : "pointer",
              opacity: alreadyHas ? 0.45 : 1,
              fontSize: 12,
              color: "rgba(255,255,255,.85)",
            }}
          >
            <input
              type="checkbox"
              checked={alreadyHas || isSelected}
              disabled={alreadyHas}
              onChange={() => !alreadyHas && toggle(charId)}
              style={{ accentColor: "#818cf8" }}
            />
            <span style={{ fontWeight: 600 }}>{p.character_name}</span>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,.4)" }}>({p.username})</span>
            {alreadyHas && (
              <span style={{ fontSize: 9, color: "#4ade80", marginLeft: "auto" }}>✓ shared</span>
            )}
          </label>
        );
      })}

      {error && <p style={{ fontSize: 11, color: "#f87171", margin: "6px 0 0" }}>{error}</p>}

      <button
        onClick={handleShare}
        disabled={busy || selected.size === 0}
        style={{
          marginTop: 10,
          width: "100%",
          padding: "6px 0",
          borderRadius: 6,
          border: "none",
          background: selected.size > 0 ? "rgba(99,102,241,.8)" : "rgba(99,102,241,.2)",
          color: selected.size > 0 ? "#fff" : "rgba(255,255,255,.4)",
          fontSize: 11,
          fontWeight: 700,
          cursor: selected.size > 0 ? "pointer" : "default",
        }}
      >
        {busy ? "Sharing…" : `Share (${selected.size})`}
      </button>
    </div>
  );

  // Portal-mode: escape overflow:hidden ancestors
  if (anchorRect) {
    return createPortal(popover, document.body);
  }
  return popover;
};

// ─── Section Row ──────────────────────────────────────────────────────────────

interface SectionRowProps {
  section: CodexSectionResponse;
  sessionId: string;
  assetId: number;
  participants: SessionParticipant[];
  onShared: () => void;
}

const SectionRow: React.FC<SectionRowProps> = ({
  section,
  sessionId,
  assetId,
  participants,
  onShared,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | undefined>();
  const shareBtnRef = useRef<HTMLButtonElement>(null);

  const alreadyShared = section.sharedWith.map((a) => a.characterId);

  const handleShare = async (characterIds: number[]) => {
    await codexService.shareSection(sessionId, assetId, section.id, characterIds);
    onShared();
  };

  const handleShareOpen = () => {
    if (shareBtnRef.current) {
      setAnchorRect(shareBtnRef.current.getBoundingClientRect());
    }
    setShareOpen((p) => !p);
  };

  return (
    <div
      style={{
        borderTop: "1px solid rgba(255,255,255,.06)",
        padding: "6px 0",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button
          onClick={() => setExpanded((p) => !p)}
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,.6)",
            cursor: "pointer",
            fontSize: 10,
            padding: 2,
          }}
        >
          {expanded ? "▼" : "▶"}
        </button>
        <span style={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,.85)", fontWeight: 600 }}>
          {section.name}
          {alreadyShared.length > 0 && (
            <span style={{ marginLeft: 5, fontSize: 9, color: "#4ade80" }}>🔓</span>
          )}
        </span>
        {/* No relative wrapper — popover escapes via portal */}
        <button
          ref={shareBtnRef}
          onClick={handleShareOpen}
          title="Share this section"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 13,
            color: "rgba(165,180,252,.6)",
            padding: "0 2px",
          }}
        >
          🔗
        </button>
        {shareOpen && (
          <SharePopover
            participants={participants}
            alreadyShared={alreadyShared}
            onShare={handleShare}
            onClose={() => setShareOpen(false)}
            anchorRect={anchorRect}
          />
        )}
      </div>
      {expanded && section.description && (
        <p
          style={{
            marginLeft: 20,
            fontSize: 11,
            color: "rgba(255,255,255,.55)",
            marginTop: 4,
            lineHeight: 1.6,
          }}
        >
          {renderDescription(section.description)}
        </p>
      )}
      {expanded && section.dmNotes && (
        <div
          style={{
            marginLeft: 20,
            marginTop: 6,
            padding: "6px 8px",
            background: "rgba(234,179,8,.06)",
            border: "1px solid rgba(234,179,8,.2)",
            borderRadius: 4,
          }}
        >
          <p
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: "rgba(234,179,8,.6)",
              letterSpacing: ".07em",
              textTransform: "uppercase",
              marginBottom: 3,
            }}
          >
            DM Notes
          </p>
          <p style={{ fontSize: 11, color: "rgba(234,179,8,.75)", lineHeight: 1.6, margin: 0 }}>
            {section.dmNotes}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Asset Detail ─────────────────────────────────────────────────────────────

interface AssetDetailProps {
  asset: CodexAsset;
  campaignId: number;
  sessionId: string;
  participants: SessionParticipant[];
  onRefresh: () => void;
}

const AssetDetail: React.FC<AssetDetailProps> = ({
  asset,
  campaignId,
  sessionId,
  participants,
  onRefresh,
}) => {
  const [shareOpen, setShareOpen] = useState(false);
  // New section form
  const [addSection, setAddSection] = useState(false);
  const [newSecName, setNewSecName] = useState("");
  const [newSecContent, setNewSecContent] = useState("");
  const [newSecDMNotes, setNewSecDMNotes] = useState("");
  const [savingSection, setSavingSection] = useState(false);
  const [sectionError, setSectionError] = useState("");

  // Fate Formulas
  const supportsFateFormulas = (type: string) =>
    type === "Character" || type === "Beast" || type === "NPC";
  const [diceTemplates, setDiceTemplates] = useState<CampaignAssetDiceTemplate[]>([]);
  const [diceTemplatesLoading, setDiceTemplatesLoading] = useState(false);
  const [rollingId, setRollingId] = useState<number | null>(null);
  // Inline form state: null = closed, -1 = creating new, n = editing template id n
  const [fateFormId, setFateFormId] = useState<number | null>(null);
  const emptyFateForm = { name: "", diceNotation: "", modifier: 0, description: "", skillType: "" };
  const [fateForm, setFateForm] = useState(emptyFateForm);
  const [savingFate, setSavingFate] = useState(false);
  const [deletingFateId, setDeletingFateId] = useState<number | null>(null);
  const [fateError, setFateError] = useState("");
  const rollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isConnected, sendDiceRoll } = useWebSocketContext();

  // Clear any pending "rolling" reset so it can't fire after unmount
  useEffect(() => () => clearTimeout(rollTimer.current ?? undefined), []);

  useEffect(() => {
    if (!supportsFateFormulas(asset.type)) {
      setDiceTemplates([]);
      return;
    }
    setDiceTemplatesLoading(true);
    CampaignAssetService.getCampaignAssetDiceTemplates(campaignId, asset.id)
      .then((data) => setDiceTemplates(data ?? []))
      .catch(() => setDiceTemplates([]))
      .finally(() => setDiceTemplatesLoading(false));
  }, [asset.id, asset.type, campaignId]);

  const handleRoll = (template: CampaignAssetDiceTemplate, isPrivate: boolean) => {
    if (!isConnected) return;
    const formula = `${template.diceNotation}${template.modifier > 0 ? `+${template.modifier}` : template.modifier < 0 ? `${template.modifier}` : ""}`;
    setRollingId(template.id);
    sendDiceRoll(formula, template.name, isPrivate, undefined, sessionId, asset.name);
    clearTimeout(rollTimer.current ?? undefined);
    rollTimer.current = setTimeout(() => setRollingId(null), 600);
  };

  const handleSaveFate = async () => {
    if (!fateForm.name.trim() || !fateForm.diceNotation.trim() || !fateForm.skillType.trim())
      return;
    setSavingFate(true);
    setFateError("");
    try {
      if (fateFormId === -1) {
        const created = await CampaignAssetService.createCampaignAssetDiceTemplate(
          campaignId,
          asset.id,
          { ...fateForm, modifier: Number(fateForm.modifier) }
        );
        setDiceTemplates((prev) => [...prev, created]);
      } else if (fateFormId !== null) {
        const updated = await CampaignAssetService.updateCampaignAssetDiceTemplate(
          campaignId,
          asset.id,
          fateFormId,
          { ...fateForm, modifier: Number(fateForm.modifier) }
        );
        setDiceTemplates((prev) => prev.map((t) => (t.id === fateFormId ? updated : t)));
      }
      setFateFormId(null);
      setFateForm(emptyFateForm);
    } catch (e: unknown) {
      setFateError(errMsg(e, "Failed to save fate formula."));
    } finally {
      setSavingFate(false);
    }
  };

  const handleDeleteFate = async (templateId: number) => {
    setDeletingFateId(templateId);
    setFateError("");
    try {
      await CampaignAssetService.deleteCampaignAssetDiceTemplate(campaignId, asset.id, templateId);
      setDiceTemplates((prev) => prev.filter((t) => t.id !== templateId));
    } catch (e: unknown) {
      setFateError(errMsg(e, "Failed to delete fate formula."));
    } finally {
      setDeletingFateId(null);
    }
  };

  const alreadyShared = asset.sharedWith.map((a) => a.characterId);

  const handleShareAsset = async (characterIds: number[]) => {
    await codexService.shareAsset(sessionId, asset.id, characterIds);
    onRefresh();
  };

  const handleAddSection = async () => {
    if (!newSecName.trim()) return;
    setSavingSection(true);
    setSectionError("");
    try {
      await CampaignAssetService.createCampaignAssetSection(campaignId, asset.id, {
        name: newSecName.trim(),
        content: newSecContent.trim(),
        dmNotes: newSecDMNotes.trim(),
        sectionOrder: asset.sections.length + 1,
      });
      setNewSecName("");
      setNewSecContent("");
      setNewSecDMNotes("");
      setAddSection(false);
      onRefresh();
    } catch (e: unknown) {
      setSectionError(errMsg(e, "Failed to add folio."));
    } finally {
      setSavingSection(false);
    }
  };

  return (
    <div
      style={{
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        height: "100%",
        overflowY: "auto",
      }}
      className="codex-scroll"
    >
      {/* Header */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 20 }}>{typeIcon(asset.type)}</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: "rgba(255,255,255,.9)", flex: 1 }}>
            {asset.name}
            {alreadyShared.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 11, color: "#4ade80" }}>🔓</span>
            )}
          </span>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 99,
              background: "rgba(99,102,241,.25)",
              color: "rgba(165,180,252,.9)",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            {asset.type}
          </span>
          {asset.category && (
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 99,
                background: "rgba(255,255,255,.06)",
                color: "rgba(255,255,255,.55)",
                fontSize: 10,
              }}
            >
              {asset.category}
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      {asset.description && (
        <p style={{ fontSize: 12, color: "rgba(255,255,255,.65)", lineHeight: 1.6 }}>
          {renderDescription(asset.description)}
        </p>
      )}

      {/* DM Notes — private, never shared */}
      {asset.dmNotes && (
        <div
          style={{
            padding: "8px 10px",
            background: "rgba(234,179,8,.06)",
            border: "1px solid rgba(234,179,8,.2)",
            borderRadius: 6,
          }}
        >
          <p
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: "rgba(234,179,8,.6)",
              letterSpacing: ".07em",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            🔒 DM Notes
          </p>
          <p style={{ fontSize: 12, color: "rgba(234,179,8,.8)", lineHeight: 1.6, margin: 0 }}>
            {asset.dmNotes}
          </p>
        </div>
      )}

      {/* Share asset button */}
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setShareOpen((p) => !p)}
          style={{
            padding: "6px 14px",
            borderRadius: 6,
            border: "1px solid rgba(99,102,241,.5)",
            background: "rgba(99,102,241,.12)",
            color: "rgba(165,180,252,.9)",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Share Asset ▾
        </button>
        {shareOpen && (
          <SharePopover
            participants={participants}
            alreadyShared={alreadyShared}
            onShare={handleShareAsset}
            onClose={() => setShareOpen(false)}
          />
        )}
      </div>

      {/* Sections */}
      <div>
        <p
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "rgba(165,180,252,.6)",
            letterSpacing: ".07em",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          Folios
        </p>
        {asset.sections.length === 0 && (
          <p style={{ fontSize: 11, color: "rgba(255,255,255,.3)" }}>No folios yet.</p>
        )}
        {asset.sections
          .slice()
          .sort((a, b) => a.sectionOrder - b.sectionOrder)
          .map((s) => (
            <SectionRow
              key={s.id}
              section={s}
              sessionId={sessionId}
              assetId={asset.id}
              participants={participants}
              onShared={onRefresh}
            />
          ))}

        {/* Add section inline form */}
        {addSection ? (
          <div
            style={{
              marginTop: 8,
              padding: "8px",
              background: "rgba(255,255,255,.04)",
              borderRadius: 6,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <input
              value={newSecName}
              onChange={(e) => setNewSecName(e.target.value)}
              placeholder="Folio name *"
              autoFocus
              style={inputStyle}
            />
            <div style={{ position: "relative" }}>
              <CustomAssetReferenceEditor
                value={newSecContent}
                onChange={(v) => setNewSecContent(v)}
                placeholder="Content (optional). Type [[ to reference an asset."
                campaignId={campaignId}
              />
            </div>
            <textarea
              value={newSecDMNotes}
              onChange={(e) => setNewSecDMNotes(e.target.value)}
              placeholder="DM Notes — private, never shared (optional)"
              rows={2}
              style={{
                ...inputStyle,
                resize: "vertical",
                borderColor: "rgba(234,179,8,.3)",
                background: "rgba(234,179,8,.05)",
              }}
            />
            {sectionError && (
              <p style={{ fontSize: 11, color: "#f87171", margin: 0 }}>{sectionError}</p>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={handleAddSection}
                disabled={savingSection || !newSecName.trim()}
                style={primaryBtnStyle}
              >
                {savingSection ? "Saving…" : "Add"}
              </button>
              <button
                onClick={() => {
                  setAddSection(false);
                  setNewSecName("");
                  setNewSecContent("");
                  setNewSecDMNotes("");
                  setSectionError("");
                }}
                style={ghostBtnStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAddSection(true)} style={{ ...ghostBtnStyle, marginTop: 8 }}>
            + Add Folio
          </button>
        )}
      </div>

      {/* Fate Formulas — Beast & Character only */}
      {supportsFateFormulas(asset.type) && (
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
            <p
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "rgba(165,180,252,.6)",
                letterSpacing: ".07em",
                textTransform: "uppercase",
                flex: 1,
                margin: 0,
              }}
            >
              Fate Formulas
            </p>
            {fateFormId === null && (
              <button
                onClick={() => {
                  setFateForm(emptyFateForm);
                  setFateFormId(-1);
                }}
                style={{
                  ...ghostBtnStyle,
                  padding: "2px 8px",
                  fontSize: 10,
                }}
              >
                + Add
              </button>
            )}
          </div>

          {fateError && (
            <p style={{ fontSize: 11, color: "#f87171", margin: "0 0 6px" }}>{fateError}</p>
          )}

          {diceTemplatesLoading && (
            <p style={{ fontSize: 11, color: "rgba(255,255,255,.3)" }}>Loading…</p>
          )}
          {!diceTemplatesLoading && diceTemplates.length === 0 && fateFormId === null && (
            <p style={{ fontSize: 11, color: "rgba(255,255,255,.3)" }}>No fate formulas yet.</p>
          )}

          {/* Inline create/edit form */}
          {fateFormId !== null && (
            <div
              style={{
                marginBottom: 8,
                padding: 8,
                background: "rgba(255,255,255,.04)",
                borderRadius: 6,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "rgba(165,180,252,.7)",
                  margin: 0,
                  textTransform: "uppercase",
                }}
              >
                {fateFormId === -1 ? "New Fate Formula" : "Edit Fate Formula"}
              </p>
              <input
                placeholder="Name *"
                value={fateForm.name}
                onChange={(e) => setFateForm((p) => ({ ...p, name: e.target.value }))}
                style={inputStyle}
                autoFocus
              />
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  placeholder="Notation (e.g. 2d6) *"
                  value={fateForm.diceNotation}
                  onChange={(e) => setFateForm((p) => ({ ...p, diceNotation: e.target.value }))}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <input
                  placeholder="Mod"
                  type="number"
                  value={fateForm.modifier}
                  onChange={(e) =>
                    setFateForm((p) => ({ ...p, modifier: parseInt(e.target.value) || 0 }))
                  }
                  style={{ ...inputStyle, width: 56 }}
                />
              </div>
              <input
                placeholder="Category / Skill type *"
                value={fateForm.skillType}
                onChange={(e) => setFateForm((p) => ({ ...p, skillType: e.target.value }))}
                style={inputStyle}
              />
              <input
                placeholder="Description (optional)"
                value={fateForm.description}
                onChange={(e) => setFateForm((p) => ({ ...p, description: e.target.value }))}
                style={inputStyle}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={handleSaveFate}
                  disabled={
                    savingFate ||
                    !fateForm.name.trim() ||
                    !fateForm.diceNotation.trim() ||
                    !fateForm.skillType.trim()
                  }
                  style={primaryBtnStyle}
                >
                  {savingFate ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => {
                    setFateFormId(null);
                    setFateForm(emptyFateForm);
                  }}
                  style={ghostBtnStyle}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Template rows */}
          {!diceTemplatesLoading &&
            diceTemplates.map((t) => {
              const diceLabel = t.diceNotation.match(/d\d+/i)?.[0] ?? t.diceNotation;
              const modStr =
                t.modifier > 0 ? `+${t.modifier}` : t.modifier < 0 ? `${t.modifier}` : "";
              const isRolling = rollingId === t.id;
              const isDeleting = deletingFateId === t.id;
              return (
                <div
                  key={t.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 0",
                    borderTop: "1px solid rgba(255,255,255,.05)",
                  }}
                >
                  {/* Dice badge */}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 34,
                      height: 34,
                      background: "rgba(245,158,11,.15)",
                      border: "1px solid rgba(245,158,11,.35)",
                      borderRadius: 4,
                      fontFamily: "monospace",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#fcd34d",
                      flexShrink: 0,
                    }}
                  >
                    {diceLabel}
                  </span>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "rgba(255,255,255,.85)",
                        margin: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.name}
                    </p>
                    <p style={{ fontSize: 10, color: "rgba(255,255,255,.4)", margin: 0 }}>
                      {t.diceNotation}
                      {modStr}
                      {t.skillType && (
                        <span style={{ marginLeft: 4, color: "rgba(165,180,252,.5)" }}>
                          · {t.skillType}
                        </span>
                      )}
                    </p>
                  </div>
                  {/* Actions */}
                  <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                    {isConnected && (
                      <>
                        <button
                          onClick={() => handleRoll(t, false)}
                          disabled={isRolling}
                          title="Roll publicly"
                          style={{
                            padding: "3px 7px",
                            borderRadius: 4,
                            border: "none",
                            background: isRolling ? "rgba(245,158,11,.3)" : "rgba(245,158,11,.8)",
                            color: "#1c1917",
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: isRolling ? "default" : "pointer",
                          }}
                        >
                          👁
                        </button>
                        <button
                          onClick={() => handleRoll(t, true)}
                          disabled={isRolling}
                          title="Roll privately"
                          style={{
                            padding: "3px 7px",
                            borderRadius: 4,
                            border: "1px solid rgba(255,255,255,.15)",
                            background: "rgba(255,255,255,.06)",
                            color: "rgba(255,255,255,.6)",
                            fontSize: 10,
                            cursor: isRolling ? "default" : "pointer",
                          }}
                        >
                          🔒
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setFateForm({
                          name: t.name,
                          diceNotation: t.diceNotation,
                          modifier: t.modifier,
                          description: t.description,
                          skillType: t.skillType,
                        });
                        setFateFormId(t.id);
                      }}
                      title="Edit"
                      style={{
                        padding: "3px 6px",
                        borderRadius: 4,
                        border: "1px solid rgba(255,255,255,.1)",
                        background: "transparent",
                        color: "rgba(255,255,255,.45)",
                        fontSize: 10,
                        cursor: "pointer",
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteFate(t.id)}
                      disabled={isDeleting}
                      title="Delete"
                      style={{
                        padding: "3px 6px",
                        borderRadius: 4,
                        border: "1px solid rgba(248,113,113,.2)",
                        background: "rgba(248,113,113,.08)",
                        color: isDeleting ? "rgba(248,113,113,.3)" : "#f87171",
                        fontSize: 10,
                        cursor: isDeleting ? "default" : "pointer",
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

// ─── Asset Tree ───────────────────────────────────────────────────────────────

interface AssetTreeProps {
  assets: CodexAsset[];
  selectedId: number | null;
  onSelect: (_id: number) => void;
}

const AssetTree: React.FC<AssetTreeProps> = ({ assets, selectedId, onSelect }) => {
  const [search, setSearch] = useState("");

  const filtered = assets.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()));

  // Group by type
  const byType = filtered.reduce<Record<string, CodexAsset[]>>((acc, a) => {
    (acc[a.type] = acc[a.type] ?? []).push(a);
    return acc;
  }, {});

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "8px 10px 4px" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search entries…"
          style={{ ...inputStyle, fontSize: 11, padding: "5px 8px" }}
        />
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 6px 8px" }} className="codex-scroll">
        {Object.entries(byType).map(([type, items]) => (
          <div key={type} style={{ marginTop: 6 }}>
            <p
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "rgba(165,180,252,.5)",
                letterSpacing: ".08em",
                textTransform: "uppercase",
                padding: "2px 6px",
              }}
            >
              {type}
            </p>
            {items.map((a) => (
              <button
                key={a.id}
                onClick={() => onSelect(a.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  width: "100%",
                  padding: "5px 8px",
                  borderRadius: 6,
                  border: "none",
                  background: selectedId === a.id ? "rgba(99,102,241,.25)" : "transparent",
                  color: selectedId === a.id ? "rgba(165,180,252,.95)" : "rgba(255,255,255,.7)",
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: 12,
                }}
              >
                <span>{typeIcon(a.type)}</span>
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {a.name}
                </span>
                {a.sharedWith.length > 0 && (
                  <span style={{ fontSize: 9, color: "#4ade80", flexShrink: 0 }}>🔓</span>
                )}
              </button>
            ))}
          </div>
        ))}
        {filtered.length === 0 && (
          <p
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,.3)",
              textAlign: "center",
              marginTop: 20,
            }}
          >
            No assets found.
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Create Asset Modal ───────────────────────────────────────────────────────

interface CreateAssetModalProps {
  campaignId: number;
  onCreated: () => void;
  onClose: () => void;
}

const ASSET_TYPES = ["Character", "Beast", "Quest", "NPC", "Location", "Item", "Lore", "Other"];

const CreateAssetModal: React.FC<CreateAssetModalProps> = ({ campaignId, onCreated, onClose }) => {
  const [name, setName] = useState("");
  const [type, setType] = useState("Other");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await CampaignAssetService.createCampaignAssetFromScratch(campaignId, {
        name: name.trim(),
        type,
        category: category.trim() || type,
        description: description.trim(),
      });
      onCreated();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to create asset.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 500,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "rgba(15,20,40,0.98)",
          border: "1px solid rgba(165,180,252,.25)",
          borderRadius: 12,
          padding: 24,
          width: 360,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <h2 style={{ fontSize: 14, fontWeight: 700, color: "rgba(165,180,252,.9)", margin: 0 }}>
          New Asset
        </h2>

        {error && <p style={{ fontSize: 11, color: "#f87171", margin: 0 }}>{error}</p>}

        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name *"
          style={inputStyle}
          autoFocus
        />

        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          style={{ ...inputStyle, cursor: "pointer", colorScheme: "dark" }}
          className="codex-select"
        >
          {ASSET_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="Category (optional)"
          style={inputStyle}
        />

        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={ghostBtnStyle}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={busy} style={primaryBtnStyle}>
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

interface AssetCodexPanelProps {
  campaignId?: number;
  sessionId: string;
  participants: SessionParticipant[];
  className?: string;
}

export const AssetCodexPanel: React.FC<AssetCodexPanelProps> = ({
  campaignId,
  sessionId,
  participants,
  className,
}) => {
  const [assets, setAssets] = useState<CodexAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) {
      // Nothing to load — don't leave the panel stuck on "Loading…"
      setAssets([]);
      setError("");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await codexService.getMyAssets(sessionId);
      setAssets(data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [sessionId, campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedAsset = assets.find((a) => a.id === selectedId) ?? null;

  if (!campaignId) {
    return (
      <div className={`text-zinc-400 text-sm p-4 ${className ?? ""}`}>
        No campaign associated with this session.
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 12px 6px",
          borderBottom: "1px solid rgba(255,255,255,.07)",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "rgba(165,180,252,.6)",
            flex: 1,
            fontWeight: 700,
            letterSpacing: ".05em",
            textTransform: "uppercase",
          }}
        >
          My Entries
        </span>
        <button
          onClick={() => setCreateOpen(true)}
          style={{
            padding: "4px 10px",
            borderRadius: 6,
            border: "1px solid rgba(99,102,241,.4)",
            background: "rgba(99,102,241,.15)",
            color: "rgba(165,180,252,.9)",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          + New
        </button>
        <button
          onClick={load}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,.4)",
            fontSize: 13,
          }}
          title="Refresh"
        >
          ↻
        </button>
      </div>

      {loading && (
        <div
          style={{ padding: 16, textAlign: "center", color: "rgba(255,255,255,.4)", fontSize: 12 }}
        >
          Loading…
        </div>
      )}
      {error && <div style={{ padding: 12, color: "#f87171", fontSize: 12 }}>{error}</div>}

      {!loading && !error && (
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Left: tree */}
          <div
            style={{
              width: 160,
              flexShrink: 0,
              borderRight: "1px solid rgba(255,255,255,.07)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <AssetTree assets={assets} selectedId={selectedId} onSelect={setSelectedId} />
          </div>

          {/* Right: detail */}
          <div style={{ flex: 1, overflow: "hidden" }}>
            {selectedAsset ? (
              <AssetDetail
                asset={selectedAsset}
                campaignId={campaignId}
                sessionId={sessionId}
                participants={participants}
                onRefresh={load}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "rgba(255,255,255,.3)",
                  fontSize: 12,
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 28 }}>📖</span>
                <span>Select an asset</span>
              </div>
            )}
          </div>
        </div>
      )}

      {createOpen && (
        <CreateAssetModal
          campaignId={campaignId}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid rgba(165,180,252,.2)",
  background: "rgba(255,255,255,.05)",
  color: "rgba(255,255,255,.85)",
  fontSize: 12,
  outline: "none",
  boxSizing: "border-box",
};

const primaryBtnStyle: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 6,
  border: "none",
  background: "rgba(99,102,241,.8)",
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 6,
  border: "1px solid rgba(255,255,255,.12)",
  background: "transparent",
  color: "rgba(255,255,255,.6)",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
};
